import React, { useState, useEffect } from "react";
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  TrendingUp, 
  Calendar, 
  ChevronLeft, 
  ChevronRight,
  CreditCard,
  Package,
  DollarSign,
  CheckCircle,
  Clock,
  Download,
  Filter,
  User,
  Users,
  Loader2,
  Pencil,
  Eye,
  Zap
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { parseTurkishNumber, formatTurkishCurrency } from "@/utils/numberUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslation } from "react-i18next";
import { DateInputTR } from "@/components/ui/date-picker-tr";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper function to format date as DD.MM.YYYY
const formatDateTR = (dateStr) => {
  if (!dateStr) return "-";
  try {
    // Handle both YYYY-MM-DD and DD/MM/YYYY formats
    let date;
    if (dateStr.includes("-")) {
      // YYYY-MM-DD format
      const [year, month, day] = dateStr.split("-");
      date = new Date(year, month - 1, day);
    } else if (dateStr.includes("/")) {
      // DD/MM/YYYY format
      const [day, month, year] = dateStr.split("/");
      date = new Date(year, month - 1, day);
    } else {
      return dateStr; // Return as-is if unknown format
    }
    
    if (isNaN(date.getTime())) return dateStr;
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch {
    return dateStr;
  }
};

export default function CommissionsModule({ businessId, isTrainerView = false, currentTrainerId = null }) {
  const { t } = useTranslation(["common", "payments", "trainers", "reports", "packages", "employees"]);
  const { formatPrice, currencySymbol } = useCurrency();
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();
  const [loading, setLoading] = useState(true);
  const [commissionData, setCommissionData] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState(currentTrainerId || "all");
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentDate, setPaymentDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showEditCommissionModal, setShowEditCommissionModal] = useState(false);
  const [editingDetail, setEditingDetail] = useState(null);
  const [editCommission, setEditCommission] = useState(""); // Direct commission amount
  const [savingCommission, setSavingCommission] = useState(false);
  
  // Employee commission states
  const [activeTab, setActiveTab] = useState("trainers");
  const [employeeCommissions, setEmployeeCommissions] = useState(null);
  
  // Bulk payment state
  const [selectedTrainerIds, setSelectedTrainerIds] = useState([]);
  const [showBulkPayModal, setShowBulkPayModal] = useState(false);
  const [bulkPayDate, setBulkPayDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [bulkPayMethod, setBulkPayMethod] = useState("cash");
  const [bulkPayAmounts, setBulkPayAmounts] = useState({}); // trainer_id -> amount
  const [bulkPayNote, setBulkPayNote] = useState("");
  const [bulkPaying, setBulkPaying] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [showEmployeePaymentModal, setShowEmployeePaymentModal] = useState(false);
  const [selectedEmployeeCommission, setSelectedEmployeeCommission] = useState(null);
  const [employeePaymentAmount, setEmployeePaymentAmount] = useState("");
  const [employeePaymentDate, setEmployeePaymentDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [employeePaymentMethod, setEmployeePaymentMethod] = useState("cash");
  const [employeePaymentNotes, setEmployeePaymentNotes] = useState("");
  const [showEditEmployeeCommissionModal, setShowEditEmployeeCommissionModal] = useState(false);
  const [editingEmployeeCommission, setEditingEmployeeCommission] = useState(null);
  const [editEmployeeCommissionValue, setEditEmployeeCommissionValue] = useState("");
  const [showEmployeeDetailsModal, setShowEmployeeDetailsModal] = useState(false);
  const [selectedEmployeeForDetails, setSelectedEmployeeForDetails] = useState(null);
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
  const [employeePaymentHistory, setEmployeePaymentHistory] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  
  // Fetch employee commission details
  const fetchEmployeeCommissionDetails = async (emp) => {
    setLoadingEmployeeDetails(true);
    try {
      const dateStr = format(selectedMonth, "yyyy-MM");
      const [year, month] = dateStr.split("-");
      const response = await fetch(
        `${API}/employees/${emp.employee_id}/my-commissions?month=${month}&year=${year}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const details = await response.json();
        // Merge summary data with details
        setSelectedEmployeeForDetails({
          ...emp,
          ...details
        });
      } else {
        // If endpoint fails, just show summary
        setSelectedEmployeeForDetails(emp);
      }
    } catch (error) {
      console.error("Error fetching employee details:", error);
      setSelectedEmployeeForDetails(emp);
    } finally {
      setLoadingEmployeeDetails(false);
    }
  };
  
  // Fetch trainers
  const fetchTrainers = async () => {
    try {
      const response = await fetch(`${API}/businesses/${businessId}/trainers`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTrainers(data);
      }
    } catch (error) {
      console.error("Error fetching trainers:", error);
    }
  };
  
  // Fetch commission data
  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const startDate = format(startOfMonth(selectedMonth), "yyyy-MM-dd");
      const endDate = format(endOfMonth(selectedMonth), "yyyy-MM-dd");
      
      let url = `${API}/businesses/${businessId}/commissions?start_date=${startDate}&end_date=${endDate}`;
      if (selectedTrainer && selectedTrainer !== "all") {
        url += `&trainer_id=${selectedTrainer}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCommissionData(data);
      }
    } catch (error) {
      console.error("Error fetching commissions:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch payment history (for the selected month, by PERIOD not payment date)
  const fetchPaymentHistory = async () => {
    try {
      const startDate = format(startOfMonth(selectedMonth), "yyyy-MM-dd");
      const endDate = format(endOfMonth(selectedMonth), "yyyy-MM-dd");
      let url = `${API}/businesses/${businessId}/commission-payments?start_date=${startDate}&end_date=${endDate}`;
      if (selectedTrainer && selectedTrainer !== "all") {
        url += `&trainer_id=${selectedTrainer}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
    }
  };
  
  // Fetch employee commissions
  const fetchEmployeeCommissions = async () => {
    try {
      const startDate = format(startOfMonth(selectedMonth), "yyyy-MM-dd");
      const endDate = format(endOfMonth(selectedMonth), "yyyy-MM-dd");
      
      const response = await fetch(
        `${API}/businesses/${businessId}/employee-commissions?start_date=${startDate}&end_date=${endDate}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setEmployeeCommissions(data);
      }
      
      // Also fetch employee payment history
      const historyResponse = await fetch(
        `${API}/businesses/${businessId}/employee-commission-payments?start_date=${startDate}&end_date=${endDate}`,
        { credentials: "include" }
      );
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setEmployeePaymentHistory(historyData);
      }
    } catch (error) {
      console.error("Error fetching employee commissions:", error);
    }
  };
  
  // Pay employee commission
  const handleEmployeeCommissionPayment = async () => {
    if (!selectedEmployeeCommission || !employeePaymentAmount) return;
    
    if (isSaving) return;
    setIsSaving(true);
    
    try {
      const response = await fetch(`${API}/businesses/${businessId}/employee-commission-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          employee_id: selectedEmployeeCommission.employee_id,
          amount: parseTurkishNumber(employeePaymentAmount),
          payment_date: employeePaymentDate,
          payment_method: employeePaymentMethod,
          notes: employeePaymentNotes
        })
      });
      
      if (response.ok) {
        toast.success(t('payments:commissionPaymentSaved'));
        setShowEmployeePaymentModal(false);
        setEmployeePaymentAmount("");
        setEmployeePaymentDate(format(new Date(), "yyyy-MM-dd"));
        setEmployeePaymentMethod("cash");
        setEmployeePaymentNotes("");
        fetchEmployeeCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:couldNotSavePayment"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };
  
  // Update employee commission manually
  const handleUpdateEmployeeCommission = async () => {
    if (!editingEmployeeCommission || editEmployeeCommissionValue === "") return;
    
    try {
      const response = await fetch(
        `${API}/businesses/${businessId}/employee-commissions/${editingEmployeeCommission.commission_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            commission: parseTurkishNumber(editEmployeeCommissionValue)
          })
        }
      );
      
      if (response.ok) {
        toast.success(t("reports:toast_primGuncellendi"));
        setShowEditEmployeeCommissionModal(false);
        setEditingEmployeeCommission(null);
        setEditEmployeeCommissionValue("");
        fetchEmployeeCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("reports:commissionUpdateFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };
  
  useEffect(() => {
    if (businessId) {
      fetchTrainers();
      fetchCommissions();
      fetchPaymentHistory();
      fetchEmployeeCommissions();
    }
  }, [businessId, selectedMonth, selectedTrainer]);
  
  // Navigate months
  const goToPrevMonth = () => setSelectedMonth(prev => subMonths(prev, 1));
  const goToNextMonth = () => setSelectedMonth(prev => addMonths(prev, 1));
  
  // Handle payment
  const handlePayment = async () => {
    if (!selectedCommission || !paymentAmount) {
      toast.error(t("common:paymentAmountRequired"));
      return;
    }
    
    if (isSaving) return;
    setIsSaving(true);
    
    try {
      const response = await fetch(`${API}/businesses/${businessId}/commission-payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          trainer_id: selectedCommission.trainer_id,
          amount: parseTurkishNumber(paymentAmount),
          period_start: format(startOfMonth(selectedMonth), "yyyy-MM-dd"),
          period_end: format(endOfMonth(selectedMonth), "yyyy-MM-dd"),
          payment_date: paymentDate,
          payment_method: paymentMethod,
          notes: paymentNotes
        })
      });
      
      if (response.ok) {
        toast.success(t('payments:commissionPaymentSaved'));
        setShowPaymentModal(false);
        setPaymentAmount("");
        setPaymentNotes("");
        setPaymentDate(format(new Date(), "yyyy-MM-dd"));
        setPaymentMethod("cash");
        setSelectedCommission(null);
        fetchCommissions();
        fetchPaymentHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:couldNotSavePayment"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setIsSaving(false);
    }
  };
  
  // ===== BULK COMMISSION PAYMENT =====
  const pendingTrainers = (commissionData?.commissions || []).filter(c => (c.pending_amount || 0) > 0);
  
  const toggleSelectAll = (checked) => {
    if (checked) {
      const ids = pendingTrainers.map(c => c.trainer_id);
      setSelectedTrainerIds(ids);
      const amts = {};
      pendingTrainers.forEach(c => { amts[c.trainer_id] = String(c.pending_amount); });
      setBulkPayAmounts(amts);
    } else {
      setSelectedTrainerIds([]);
      setBulkPayAmounts({});
    }
  };
  
  const toggleSelect = (trainer_id, pending) => {
    setSelectedTrainerIds(prev => {
      if (prev.includes(trainer_id)) {
        const next = prev.filter(id => id !== trainer_id);
        setBulkPayAmounts(amt => { const cp = { ...amt }; delete cp[trainer_id]; return cp; });
        return next;
      }
      setBulkPayAmounts(amt => ({ ...amt, [trainer_id]: String(pending) }));
      return [...prev, trainer_id];
    });
  };
  
  const openBulkPayModal = () => {
    if (selectedTrainerIds.length === 0) {
      toast.error("Lütfen en az bir antrenör seçin");
      return;
    }
    setBulkPayDate(format(new Date(), "yyyy-MM-dd"));
    setBulkPayMethod("cash");
    setBulkPayNote("");
    setShowBulkPayModal(true);
  };
  
  const handleBulkPay = async () => {
    if (bulkPaying) return;
    const items = selectedTrainerIds.map(tid => ({
      trainer_id: tid,
      amount: parseTurkishNumber(bulkPayAmounts[tid] || "0"),
    })).filter(it => it.amount > 0);
    if (items.length === 0) {
      toast.error("Geçerli tutar girilmemiş");
      return;
    }
    setBulkPaying(true);
    let okCount = 0, failCount = 0;
    const errors = [];
    for (const it of items) {
      try {
        const resp = await fetch(`${API}/businesses/${businessId}/commission-payments`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            trainer_id: it.trainer_id,
            amount: it.amount,
            period_start: format(startOfMonth(selectedMonth), "yyyy-MM-dd"),
            period_end: format(endOfMonth(selectedMonth), "yyyy-MM-dd"),
            payment_date: bulkPayDate,
            payment_method: bulkPayMethod,
            notes: bulkPayNote || "Toplu prim ödemesi",
          }),
        });
        if (resp.ok) okCount++;
        else {
          failCount++;
          const err = await resp.json().catch(() => ({}));
          const trainerName = pendingTrainers.find(p => p.trainer_id === it.trainer_id)?.name || it.trainer_id;
          errors.push(`${trainerName}: ${err.detail || "hata"}`);
        }
      } catch (e) {
        failCount++;
      }
    }
    setBulkPaying(false);
    if (okCount > 0) toast.success(`${okCount} antrenöre ödeme yapıldı`);
    if (failCount > 0) toast.error(`${failCount} ödeme başarısız: ${errors.join("; ").slice(0, 200)}`);
    setShowBulkPayModal(false);
    setSelectedTrainerIds([]);
    setBulkPayAmounts({});
    fetchCommissions();
    fetchPaymentHistory();
  };
  
  // Handle commission edit
  const handleEditCommission = (detail, type) => {
    // detail: card_detail or package_detail object
    // type: "card" or "package"
    setEditingDetail({ ...detail, detailType: type });
    setEditCommission(detail.commission?.toString() || "0");
    setShowEditCommissionModal(true);
  };
  
  const saveCommissionEdit = async () => {
    if (!editingDetail?.reservation_id) {
      toast.error(t("common:toast_rezervasyonBilgisiBulunamadi"));
      return;
    }
    
    setSavingCommission(true);
    try {
      const response = await fetch(`${API}/reservations/${editingDetail.reservation_id}/commission`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          commission: parseTurkishNumber(editCommission) // Send direct commission amount
        })
      });
      
      if (response.ok) {
        toast.success(t("reports:toast_primTutariGuncellendi"));
        setShowEditCommissionModal(false);
        setEditingDetail(null);
        // Close details modal and refresh data
        setShowDetailsModal(false);
        setSelectedCommission(null);
        await fetchCommissions();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('settings:updateFailed'));
      }
    } catch (error) {
      console.error("Error saving commission:", error);
      toast.error(t('common:errorOccurred'));
    } finally {
      setSavingCommission(false);
    }
  };
  
  // Calculate totals
  const totals = commissionData?.commissions?.reduce((acc, c) => ({
    sessions: acc.sessions + (c.total_sessions || 0),
    total: acc.total + (c.total_commission || 0),
    paid: acc.paid + (c.paid_amount || 0),
    pending: acc.pending + (c.pending_amount || 0)
  }), { sessions: 0, total: 0, paid: 0, pending: 0 }) || { sessions: 0, total: 0, paid: 0, pending: 0 };
  
  // Calculate employee totals
  const employeeTotals = employeeCommissions?.summary || { total_commission: 0, paid_amount: 0, pending_amount: 0 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-navy flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            {t('reports:commissionManagement')}
          </h2>
          <p className="text-sm text-grey">{t("reports:antrenorVeCalisanPrimleriniHesapla")}</p>
        </div>
        
        {/* Month Navigator */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPrevMonth}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="px-4 py-2 bg-slate-100 rounded-lg min-w-[150px] text-center font-medium">
            {format(selectedMonth, "MMMM yyyy", { locale: getDateLocale() })}
          </div>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Tabs for Trainers and Employees */}
      {!isTrainerView && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-100 rounded-xl p-1">
            <TabsTrigger value="trainers" className="rounded-lg data-[state=active]:bg-white flex items-center gap-2">
              <User className="w-4 h-4" />
              {t("reports:trainerCommissions")}
            </TabsTrigger>
            <TabsTrigger value="employees" className="rounded-lg data-[state=active]:bg-white flex items-center gap-2">
              <Users className="w-4 h-4" />
              {t("reports:employeeCommissions")}
            </TabsTrigger>
          </TabsList>
          
          {/* Trainer Commissions Tab */}
          <TabsContent value="trainers" className="mt-4 space-y-4">
            {/* Trainer Filters */}
            {trainers.length > 0 && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-grey" />
                  <Label className="text-sm">{t("common:trainerLabel2")}:</Label>
                </div>
                <Select value={selectedTrainer} onValueChange={setSelectedTrainer}>
                  <SelectTrigger className="w-[200px] rounded-xl">
                    <SelectValue placeholder={t('trainers:allTrainers')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('trainers:allTrainers')}</SelectItem>
                    {trainers.map(t => (
                      <SelectItem key={t.trainer_id} value={t.trainer_id}>
                        {t.name} {t.surname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 mb-1">{t('common:totalLessons', 'Toplam Ders')}</p>
                <p className="text-2xl font-bold text-blue-700">{totals.sessions}</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 mb-1">{t("reports:totalCommission")}</p>
                <p className="text-2xl font-bold text-purple-700">{currencySymbol}{totals.total.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 mb-1">{t("common:paidAmount")}</p>
                <p className="text-2xl font-bold text-green-700">{currencySymbol}{totals.paid.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-orange-600 mb-1">{t("common:pending")}</p>
                <p className="text-2xl font-bold text-orange-700">{currencySymbol}{totals.pending.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</p>
              </div>
              <Clock className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Commission Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("reports:antrenorPrimleri")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-navy" />
            </div>
          ) : commissionData?.commissions?.length > 0 ? (
            <div className="overflow-x-auto">
              {/* Bulk action bar */}
              {!isTrainerView && selectedTrainerIds.length > 0 && (
                <div className="flex items-center justify-between gap-3 mb-3 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                  <p className="text-sm font-medium text-emerald-800">
                    <Zap className="inline w-4 h-4 mr-1" />
                    <b>{selectedTrainerIds.length}</b> antrenör seçildi — toplam{" "}
                    <b>
                      {currencySymbol}
                      {Object.entries(bulkPayAmounts)
                        .filter(([id]) => selectedTrainerIds.includes(id))
                        .reduce((sum, [, v]) => sum + parseTurkishNumber(v || "0"), 0)
                        .toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                    </b>
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg"
                      onClick={() => { setSelectedTrainerIds([]); setBulkPayAmounts({}); }}
                    >
                      Temizle
                    </Button>
                    <Button
                      size="sm"
                      className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={openBulkPayModal}
                      data-testid="bulk-pay-open-btn"
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Toplu Prim Öde ({selectedTrainerIds.length})
                    </Button>
                  </div>
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    {!isTrainerView && (
                      <TableHead className="w-10">
                        <Checkbox
                          checked={pendingTrainers.length > 0 && selectedTrainerIds.length === pendingTrainers.length}
                          onCheckedChange={toggleSelectAll}
                          data-testid="bulk-pay-select-all"
                        />
                      </TableHead>
                    )}
                    <TableHead className="whitespace-nowrap">{t('common:trainer')}</TableHead>
                    <TableHead className="text-center whitespace-nowrap">{t("common:lessonLabel")}</TableHead>
                    <TableHead className="text-center whitespace-nowrap">{t("common:cardLabel")}</TableHead>
                    <TableHead className="text-center whitespace-nowrap">{t("packages:package")}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t("reports:totalCommission")}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t("common:paidAmount")}</TableHead>
                    <TableHead className="text-right whitespace-nowrap">{t("common:pending")}</TableHead>
                    <TableHead className="text-right whitespace-nowrap" title="Tüm zamanların toplam kalan prim borcu">Kalan (Kümülatif)</TableHead>
                    <TableHead className="text-center whitespace-nowrap">{t("common:i̇slem")}</TableHead>
                  </TableRow>
                </TableHeader>
              <TableBody>
                {commissionData.commissions.map(comm => (
                  <TableRow key={comm.trainer_id}>
                    {!isTrainerView && (
                      <TableCell className="w-10">
                        {(comm.pending_amount || 0) > 0 && (
                          <Checkbox
                            checked={selectedTrainerIds.includes(comm.trainer_id)}
                            onCheckedChange={() => toggleSelect(comm.trainer_id, comm.pending_amount)}
                            data-testid={`bulk-pay-row-${comm.trainer_id}`}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-grey" />
                        <span className="font-medium">{comm.name}</span>
                      </div>
                      <p className="text-xs text-grey mt-0.5">
                        {comm.calculation_method === "per_package_sale" ? `📦 ${t("packages:packageSale")}` : `📚 ${t("common:perLesson")}`}
                      </p>
                    </TableCell>
                    <TableCell className="text-center">{comm.total_sessions}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                        {comm.card_sessions} / {currencySymbol}{comm.card_commission?.toLocaleString('tr-TR') || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                        {comm.package_sessions} / {currencySymbol}{comm.package_commission?.toLocaleString('tr-TR') || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-bold text-purple-700">
                      {currencySymbol}{comm.total_commission?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right text-green-600">
                      {currencySymbol}{comm.paid_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right">
                      {comm.pending_amount > 0 ? (
                        <span className="text-orange-600 font-medium">
                          {currencySymbol}{comm.pending_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-green-600">✓</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right" data-testid={`cumulative-remaining-${comm.trainer_id}`}>
                      {(comm.cumulative_remaining || 0) > 0 ? (
                        <span className="text-red-600 font-semibold">
                          {currencySymbol}{comm.cumulative_remaining?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (comm.cumulative_remaining || 0) < 0 ? (
                        <span className="text-blue-600 font-semibold" title="Fazla ödenmiş (sonraki aylara sayılır)">
                          +{currencySymbol}{Math.abs(comm.cumulative_remaining)?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-green-600">✓</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 px-2"
                          onClick={() => {
                            setSelectedCommission(comm);
                            setShowDetailsModal(true);
                          }}
                        >
                          {t("common:detail")}
                        </Button>
                        {!isTrainerView && comm.pending_amount > 0 && (
                          <Button
                            size="sm"
                            className="h-8 px-2 bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedCommission(comm);
                              setPaymentAmount(comm.pending_amount.toString());
                              setShowPaymentModal(true);
                            }}
                          >
                            {t("common:pay")}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-grey">
              {t("common:noCompletedLessonsThisPeriod")}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('payments:paymentHistory')}</CardTitle>
          </CardHeader>
          <CardContent className="p-0 sm:p-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">{t('common:date')}</TableHead>
                    <TableHead className="whitespace-nowrap">{t('common:trainer')}</TableHead>
                    <TableHead className="whitespace-nowrap">Dönem</TableHead>
                  <TableHead className="text-right">{t('common:amount')}</TableHead>
                  <TableHead>{t('common:note')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paymentHistory.slice(0, 10).map(p => (
                  <TableRow key={p.payment_id}>
                    <TableCell>{formatDateTR(p.payment_date?.split('T')[0])}</TableCell>
                    <TableCell>{p.trainer_name}</TableCell>
                    <TableCell className="text-sm text-grey">
                      {formatDateTR(p.period_start)} - {formatDateTR(p.period_end)}
                    </TableCell>
                    <TableCell className="text-right font-medium text-green-600 whitespace-nowrap">
                      {currencySymbol}{p.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-sm text-grey">{p.notes || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("reports:primOdemesi")}</DialogTitle>
          </DialogHeader>
          {selectedCommission && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium">{selectedCommission.name}</p>
                <p className="text-sm text-grey">
                  {format(selectedMonth, "MMMM yyyy", { locale: getDateLocale() })} {t("common:period")}
                </p>
                <p className="text-lg font-bold text-purple-700 mt-2">
                  {t("common:pending")}: {currencySymbol}{selectedCommission.pending_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
                {typeof selectedCommission.cumulative_remaining === 'number' && (
                  <p className="text-sm text-red-600 mt-1">
                    Toplam Kalan (kümülatif): {currencySymbol}{selectedCommission.cumulative_remaining?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>
              
              <div>
                <Label>{t("common:paymentAmountLabel", { currency: currencySymbol })}</Label>
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder={t("common:paymentAmountPlaceholder", "Tutar girin")}
                />
              </div>

              <div>
                <Label>{t("common:collectionDate")}</Label>
                <DatePickerTR
                  value={paymentDate}
                  onChange={(ds) => ds && setPaymentDate(ds)}
                  returnDateString
                  placeholder="Tarih seç"
                  data-testid="commission-payment-date"
                />
              </div>

              <div>
                <Label>{t('payments:paymentMethod')}</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                    <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                    <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                    <SelectItem value="eft">{t('payments:eft')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t("common:noteOptional")}</Label>
                <Input
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder={t("common:paymentNote")}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)} disabled={isSaving}>{t('common:cancel')}</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handlePayment} disabled={isSaving}>
              {isSaving ? t("common:saving") : t("common:savePayment2")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle>{t("reports:commissionDetail")} - {selectedCommission?.name}</DialogTitle>
          </DialogHeader>
          {selectedCommission && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="p-2 sm:p-3 bg-amber-50 rounded-xl text-center">
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-amber-600 mb-1" />
                  <p className="text-xs text-amber-600">Kart Primi</p>
                  <p className="font-bold text-amber-700 text-sm sm:text-base">{currencySymbol}{selectedCommission.card_commission?.toLocaleString('tr-TR')}</p>
                  <p className="text-xs text-grey">{selectedCommission.card_sessions} ders</p>
                </div>
                <div className="p-2 sm:p-3 bg-emerald-50 rounded-xl text-center">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-emerald-600 mb-1" />
                  <p className="text-xs text-emerald-600">Paket Primi</p>
                  <p className="font-bold text-emerald-700 text-sm sm:text-base">{currencySymbol}{selectedCommission.package_commission?.toLocaleString('tr-TR')}</p>
                  <p className="text-xs text-grey">{selectedCommission.package_sessions} ders</p>
                </div>
                <div className="p-2 sm:p-3 bg-purple-50 rounded-xl text-center">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-purple-600 mb-1" />
                  <p className="text-xs text-purple-600">{t('common:total')}</p>
                  <p className="font-bold text-purple-700 text-sm sm:text-base">{currencySymbol}{selectedCommission.total_commission?.toLocaleString('tr-TR')}</p>
                  <p className="text-xs text-grey">{selectedCommission.total_sessions} ders</p>
                </div>
              </div>
              
              {/* Card Details */}
              {selectedCommission.card_details?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    {t("reports:cardLessons")}
                  </h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">{t('common:date')}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:member")}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:cardLabel")}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:type")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t('common:amount')}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("common:rate")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("reports:commissionLabel")}</TableHead>
                          {!isTrainerView && <TableHead className="text-center whitespace-nowrap">{t("common:i̇slem")}</TableHead>}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedCommission.card_details.map((d, i) => (
                        <React.Fragment key={i}>
                        <TableRow>
                          <TableCell>{formatDateTR(d.date)}</TableCell>
                          <TableCell>{d.member_name}</TableCell>
                          <TableCell><Badge variant="secondary">{d.card_name}</Badge></TableCell>
                          <TableCell>{getLocalizedTrainingType(d.training_type)}</TableCell>
                          <TableCell className="text-right">{currencySymbol}{d.session_price?.toLocaleString('tr-TR')}</TableCell>
                          <TableCell className="text-right">
                            {d.rate_type === "percentage" ? `%${d.rate}` : `${currencySymbol}${d.rate}`}
                            {d.manual_override && (
                              <Badge variant="outline" className="ml-1 text-xs bg-blue-50 text-blue-600">{t("common:manual")}</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-medium text-amber-700">
                            {currencySymbol}{d.commission?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </TableCell>
                          {!isTrainerView && (
                            <TableCell className="text-center">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditCommission(d, "card")}
                                className="h-7 w-7 p-0"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                        {d.lesson_note && (
                          <TableRow className="bg-amber-50/50">
                            <TableCell colSpan={isTrainerView ? 7 : 8} className="text-xs italic text-amber-900 py-1.5">
                              <span className="font-medium not-italic">📝 {t("reservations:lessonNote", "Ders Açıklaması")}:</span> {d.lesson_note}
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </div>
              )}
              
              {/* Package Details */}
              {selectedCommission.package_details?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {t("reports:packageLessons")}
                  </h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">{t('common:date')}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:member")}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("packages:package")}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:type")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t('common:amount')}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("common:rate")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("reports:commissionLabel")}</TableHead>
                          {!isTrainerView && <TableHead className="text-center whitespace-nowrap">{t("common:i̇slem")}</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedCommission.package_details.map((d, i) => (
                          <React.Fragment key={i}>
                          <TableRow>
                            <TableCell className="whitespace-nowrap">{formatDateTR(d.date)}</TableCell>
                            <TableCell className="whitespace-nowrap">{d.member_name}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{d.package_name}</Badge>
                              {d.package_no && (
                                <span className="ml-1 font-mono text-xs text-grey-600">{d.package_no}</span>
                              )}
                              {d.calculation_method === "per_package_sale" && (
                                <Badge className="ml-1 bg-blue-100 text-blue-700 text-xs">{t('members:packageSale')}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{getLocalizedTrainingType(d.training_type)}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {d.calculation_method === "per_package_sale" 
                                ? `${currencySymbol}${d.package_price?.toLocaleString()} (${d.total_sessions})`
                                : `${currencySymbol}${(d.session_price || 0).toLocaleString()}`}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {d.rate_type === "percentage" ? `%${d.rate}` : `${currencySymbol}${d.rate}`}
                              {d.manual_override && (
                                <Badge variant="outline" className="ml-1 text-xs bg-blue-50 text-blue-600">{t("common:manual")}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium text-emerald-700 whitespace-nowrap">
                              {currencySymbol}{d.commission?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </TableCell>
                            {!isTrainerView && (
                              <TableCell className="text-center">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleEditCommission(d, "package")}
                                className="h-7 w-7 p-0"
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                        {d.lesson_note && (
                          <TableRow className="bg-emerald-50/50">
                            <TableCell colSpan={isTrainerView ? 7 : 8} className="text-xs italic text-emerald-900 py-1.5">
                              <span className="font-medium not-italic">📝 {t("reservations:lessonNote", "Ders Açıklaması")}:</span> {d.lesson_note}
                            </TableCell>
                          </TableRow>
                        )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Commission Modal */}
      <Dialog open={showEditCommissionModal} onOpenChange={setShowEditCommissionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("reports:primDuzenle")}</DialogTitle>
          </DialogHeader>
          {editingDetail && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-grey">{t("common:date")}:</span>
                  <span>{editingDetail.date}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-grey">{t("common:type")}:</span>
                  <span>{getLocalizedTrainingType(editingDetail.training_type)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-grey">{t("common:amount")}:</span>
                  <span>{currencySymbol}{editingDetail.session_price?.toLocaleString('tr-TR')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-grey">{t("reports:currentCommission")}:</span>
                  <span className="font-bold text-purple-700">{currencySymbol}{editingDetail.commission?.toLocaleString('tr-TR')}</span>
                </div>
              </div>
              
              <div>
                <Label>{t("reports:newCommissionAmount")}</Label>
                <Input
                  type="number"
                  value={editCommission}
                  onChange={(e) => setEditCommission(e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder={t("reports:newCommissionAmount")}
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="p-3 bg-amber-50 rounded-xl text-sm text-amber-700">
                {t("reports:commissionChangeNote")}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCommissionModal(false)}>
              {t("common:cancel")}
            </Button>
            <Button 
              onClick={saveCommissionEdit}
              disabled={savingCommission || !editCommission}
              className="bg-navy hover:bg-navy/90"
            >
              {savingCommission && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
          {/* Close Trainer Tab Content */}
          </TabsContent>
          
          {/* Employee Commissions Tab */}
          <TabsContent value="employees" className="mt-4 space-y-4">
            {/* Employee Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-purple-600 mb-1">{t("reports:totalCommission")}</p>
                      <p className="text-2xl font-bold text-purple-700">
                        {currencySymbol}{employeeTotals.total_commission?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-green-600 mb-1">{t("common:paidAmount")}</p>
                      <p className="text-2xl font-bold text-green-700">
                        {currencySymbol}{employeeTotals.paid_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-orange-600 mb-1">{t("common:pending")}</p>
                      <p className="text-2xl font-bold text-orange-700">
                        {currencySymbol}{employeeTotals.pending_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <Clock className="w-8 h-8 text-orange-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Employee Commission Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t("reports:calisanPrimleri")}</CardTitle>
              </CardHeader>
              <CardContent className="p-0 sm:p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-navy" />
                  </div>
                ) : employeeCommissions?.employees?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">{t("employees:employeeLabel")}</TableHead>
                          <TableHead className="text-center whitespace-nowrap">{t("common:lessonLabel")}</TableHead>
                          <TableHead className="text-center whitespace-nowrap">{t("common:cardLabel")}</TableHead>
                          <TableHead className="text-center whitespace-nowrap">{t("packages:package")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("reports:totalCommission")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("common:paidAmount")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("common:pending")}</TableHead>
                          <TableHead className="text-center whitespace-nowrap">{t("common:i̇slem")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employeeCommissions.employees.map((emp) => (
                          <TableRow key={emp.employee_id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-grey" />
                                <span className="font-medium">{emp.name}</span>
                              </div>
                              <p className="text-xs text-grey mt-0.5">{emp.position || t("employees:employeeLabel")}</p>
                            </TableCell>
                            <TableCell className="text-center">{emp.total_sessions}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                                {emp.card_sessions || 0} / {currencySymbol}{(emp.card_commission || 0).toLocaleString('tr-TR')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                                {emp.package_sessions || 0} / {currencySymbol}{(emp.package_commission || 0).toLocaleString('tr-TR')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-bold text-purple-700">
                              {currencySymbol}{emp.total_commission?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-right text-green-600">
                              {currencySymbol}{emp.paid_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-right">
                              {emp.pending_amount > 0 ? (
                                <span className="text-orange-600 font-medium">
                                  {currencySymbol}{emp.pending_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                </span>
                              ) : (
                                <span className="text-green-600">✓</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 px-2"
                                  onClick={() => {
                                    fetchEmployeeCommissionDetails(emp);
                                    setShowEmployeeDetailsModal(true);
                                  }}
                                  data-testid={`employee-commission-details-${emp.employee_id}`}
                                >
                                  {t("common:detail")}
                                </Button>
                                {emp.pending_amount > 0 && (
                                  <Button
                                    size="sm"
                                    className="h-8 px-2 bg-green-600 hover:bg-green-700"
                                    onClick={() => {
                                      setSelectedEmployeeCommission(emp);
                                      setEmployeePaymentAmount(emp.pending_amount.toString());
                                      setShowEmployeePaymentModal(true);
                                    }}
                                  >
                                    {t("common:pay")}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-grey">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{t("reports:buDonemdeCalisanPrimiBulunamadi")}</p>
                    <p className="text-xs mt-1">{t("reports:primAlanCalisanlarDersTamamlandiginda")}</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Employee Payment History */}
            {employeePaymentHistory.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{t('payments:paymentHistory')}</CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">{t('common:date')}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("employees:employeeLabel")}</TableHead>
                          <TableHead className="whitespace-nowrap">Dönem</TableHead>
                          <TableHead className="text-right">{t('common:amount')}</TableHead>
                          <TableHead>{t('common:note')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {employeePaymentHistory.slice(0, 10).map(p => (
                          <TableRow key={p.payment_id}>
                            <TableCell>{format(new Date(p.payment_date), "dd/MM/yyyy")}</TableCell>
                            <TableCell className="font-medium">{p.employee_name}</TableCell>
                            <TableCell className="text-sm text-grey">
                              {p.period_start || format(startOfMonth(selectedMonth), "dd/MM/yyyy")} - {p.period_end || format(endOfMonth(selectedMonth), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell className="text-right font-medium text-green-600 whitespace-nowrap">
                              {currencySymbol}{p.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className="text-sm text-grey">{p.notes || "-"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
      
      {/* Employee Payment Modal */}
      <Dialog open={showEmployeePaymentModal} onOpenChange={setShowEmployeePaymentModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("reports:calisanPrimOdemesi")}</DialogTitle>
          </DialogHeader>
          {selectedEmployeeCommission && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="font-medium">{selectedEmployeeCommission.name}</p>
                <p className="text-sm text-grey">
                  {format(selectedMonth, "MMMM yyyy", { locale: getDateLocale() })} {t("common:period")}
                </p>
                <p className="text-lg font-bold text-purple-700 mt-2">
                  {t("common:pending")}: {currencySymbol}{selectedEmployeeCommission.pending_amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              
              <div>
                <Label>{t("common:paymentAmountLabel", { currency: currencySymbol })}</Label>
                <Input
                  type="number"
                  value={employeePaymentAmount}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    if (val <= selectedEmployeeCommission.pending_amount) {
                      setEmployeePaymentAmount(e.target.value);
                    }
                  }}
                  max={selectedEmployeeCommission.pending_amount}
                  className="rounded-xl mt-1"
                  placeholder={t("common:paymentAmountPlaceholder", "Tutar girin")}
                />
                <p className="text-xs text-grey mt-1">
                  {t("common:maxLabel", "Maksimum")}: {currencySymbol}{selectedEmployeeCommission.pending_amount?.toLocaleString('tr-TR')}
                </p>
              </div>

              <div>
                <Label>{t("common:collectionDate")}</Label>
                <DateInputTR value={employeePaymentDate}
         onChange={(e) => setEmployeePaymentDate(e.target.value)}
         className="rounded-xl mt-1" />
              </div>

              <div>
                <Label>{t('payments:paymentMethod')}</Label>
                <Select value={employeePaymentMethod} onValueChange={setEmployeePaymentMethod}>
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('payments:cash')}</SelectItem>
                    <SelectItem value="credit_card">{t('payments:creditCard')}</SelectItem>
                    <SelectItem value="bank_transfer">{t('payments:bankTransfer')}</SelectItem>
                    <SelectItem value="eft">{t('payments:eft')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t("common:noteOptional")}</Label>
                <Input
                  value={employeePaymentNotes}
                  onChange={(e) => setEmployeePaymentNotes(e.target.value)}
                  className="rounded-xl mt-1"
                  placeholder={t("common:paymentNote")}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeePaymentModal(false)} disabled={isSaving}>{t('common:cancel')}</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleEmployeeCommissionPayment} disabled={isSaving}>
              {isSaving ? t("common:saving") : t("common:savePayment2")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Commission Details Modal */}
      <Dialog open={showEmployeeDetailsModal} onOpenChange={setShowEmployeeDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {selectedEmployeeForDetails?.name} - {t("reports:commissionDetails")}
            </DialogTitle>
          </DialogHeader>
          {loadingEmployeeDetails ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-2 text-grey">{t('common:loading')}</span>
            </div>
          ) : selectedEmployeeForDetails && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="p-2 sm:p-3 bg-amber-50 rounded-xl text-center">
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-amber-600 mb-1" />
                  <p className="text-xs text-amber-600">Kart Primi</p>
                  <p className="font-bold text-amber-700 text-sm sm:text-base">
                    {currencySymbol}{(selectedEmployeeForDetails.card_commission || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-grey">{selectedEmployeeForDetails.card_sessions || 0} ders</p>
                </div>
                <div className="p-2 sm:p-3 bg-emerald-50 rounded-xl text-center">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-emerald-600 mb-1" />
                  <p className="text-xs text-emerald-600">Paket Primi</p>
                  <p className="font-bold text-emerald-700 text-sm sm:text-base">
                    {currencySymbol}{(selectedEmployeeForDetails.package_commission || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-grey">{selectedEmployeeForDetails.package_sessions || 0} ders</p>
                </div>
                <div className="p-2 sm:p-3 bg-purple-50 rounded-xl text-center">
                  <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mx-auto text-purple-600 mb-1" />
                  <p className="text-xs text-purple-600">{t('common:total')}</p>
                  <p className="font-bold text-purple-700 text-sm sm:text-base">
                    {currencySymbol}{(selectedEmployeeForDetails.total_commission || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-grey">{selectedEmployeeForDetails.lesson_count || 0} ders</p>
                </div>
              </div>

              {/* Card Details */}
              {selectedEmployeeForDetails.card_details?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    {t("reports:cardLessons")}
                  </h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">{t('common:date')}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:member")}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:cardLabel")}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:type")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t('common:amount')}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("common:rate")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("reports:commissionLabel")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedEmployeeForDetails.card_details.map((d, i) => (
                          <TableRow key={i}>
                            <TableCell className="whitespace-nowrap">{formatDateTR(d.date || d.lesson_date)}</TableCell>
                            <TableCell className="whitespace-nowrap">{d.member_name || "-"}</TableCell>
                            <TableCell><Badge variant="secondary" className="bg-amber-50 text-amber-700">{d.card_name}</Badge></TableCell>
                            <TableCell className="whitespace-nowrap">{getLocalizedTrainingType(d.training_type)}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{currencySymbol}{(d.session_price || 0).toLocaleString('tr-TR')}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {d.rate_type === "percentage" ? `%${d.rate}` : `${currencySymbol}${d.rate}`}
                            </TableCell>
                            <TableCell className="text-right font-medium text-amber-700 whitespace-nowrap">
                              {currencySymbol}{(d.commission || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {/* Package Details */}
              {selectedEmployeeForDetails.package_details?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {t("reports:packageLessons")}
                  </h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">{t('common:date')}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:member")}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("packages:package")}</TableHead>
                          <TableHead className="whitespace-nowrap">{t("common:type")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t('common:amount')}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("common:rate")}</TableHead>
                          <TableHead className="text-right whitespace-nowrap">{t("reports:commissionLabel")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedEmployeeForDetails.package_details.map((d, i) => (
                          <TableRow key={i}>
                            <TableCell className="whitespace-nowrap">{formatDateTR(d.date || d.lesson_date)}</TableCell>
                            <TableCell className="whitespace-nowrap">{d.member_name || "-"}</TableCell>
                            <TableCell><Badge variant="secondary" className="bg-emerald-50 text-emerald-700">{d.package_name || "Paket"}</Badge>{d.package_no && (<span className="ml-1 font-mono text-xs text-grey-600">{d.package_no}</span>)}</TableCell>
                            <TableCell className="whitespace-nowrap">{getLocalizedTrainingType(d.training_type)}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">{currencySymbol}{(d.session_price || 0).toLocaleString('tr-TR')}</TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {d.rate_type === "percentage" ? `%${d.rate}` : `${currencySymbol}${d.rate}`}
                            </TableCell>
                            <TableCell className="text-right font-medium text-emerald-700 whitespace-nowrap">
                              {currencySymbol}{(d.commission || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* No data message */}
              {(!selectedEmployeeForDetails.card_details?.length && !selectedEmployeeForDetails.package_details?.length) && (
                <div className="text-center py-6 text-grey">
                  <p>{t("reports:detayliPrimKaydiBulunamadi")}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeDetailsModal(false)}>{t('common:close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Commission Payment Modal */}
      <Dialog open={showBulkPayModal} onOpenChange={setShowBulkPayModal}>
        <DialogContent className="max-w-2xl" data-testid="bulk-pay-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-emerald-600" />
              Toplu Prim Ödemesi ({selectedTrainerIds.length} antrenör)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-2 max-h-[60vh] overflow-y-auto">
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-800">
              Her antrenör için ödenecek tutarı düzenleyebilirsiniz. Tutar = 0 olan kayıtlar atlanır.
              <br />
              <span className="text-xs">Dönem: <b>{format(selectedMonth, "MMMM yyyy", { locale: tr })}</b></span>
            </div>
            
            <div className="rounded-xl border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Antrenör</TableHead>
                    <TableHead className="text-right">Bekleyen</TableHead>
                    <TableHead className="text-right w-[180px]">Ödenecek ({currencySymbol})</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedTrainerIds.map(tid => {
                    const comm = pendingTrainers.find(c => c.trainer_id === tid);
                    if (!comm) return null;
                    return (
                      <TableRow key={tid}>
                        <TableCell className="font-medium">{comm.name}</TableCell>
                        <TableCell className="text-right text-orange-600">
                          {currencySymbol}{(comm.pending_amount || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            value={bulkPayAmounts[tid] || ""}
                            onChange={(e) => setBulkPayAmounts(prev => ({ ...prev, [tid]: e.target.value }))}
                            className="rounded-lg h-8 text-right"
                            max={comm.pending_amount}
                            min="0"
                            data-testid={`bulk-pay-amount-${tid}`}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ödeme Tarihi</Label>
                <DateInputTR value={bulkPayDate}
         onChange={(e) => setBulkPayDate(e.target.value)}
         className="rounded-xl"
         data-testid="bulk-pay-date" />
              </div>
              <div>
                <Label>Yöntem</Label>
                <Select value={bulkPayMethod} onValueChange={setBulkPayMethod}>
                  <SelectTrigger className="rounded-xl" data-testid="bulk-pay-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Nakit</SelectItem>
                    <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                    <SelectItem value="bank_transfer">Havale / EFT</SelectItem>
                    <SelectItem value="other">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Not (opsiyonel)</Label>
              <Input
                value={bulkPayNote}
                onChange={(e) => setBulkPayNote(e.target.value)}
                placeholder="Toplu prim ödemesi - Mayıs 2026"
                className="rounded-xl"
                data-testid="bulk-pay-note"
              />
            </div>
            
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 flex justify-between items-center">
              <span className="font-medium text-emerald-800">Toplam Ödeme</span>
              <span className="text-xl font-bold text-emerald-700">
                {currencySymbol}
                {Object.entries(bulkPayAmounts)
                  .filter(([id]) => selectedTrainerIds.includes(id))
                  .reduce((sum, [, v]) => sum + parseTurkishNumber(v || "0"), 0)
                  .toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkPayModal(false)} disabled={bulkPaying}>
              Vazgeç
            </Button>
            <Button
              onClick={handleBulkPay}
              disabled={bulkPaying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bulk-pay-submit"
            >
              {bulkPaying ? "Ödeniyor..." : `${selectedTrainerIds.length} Ödeme Yap`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
